import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// ShareButton — copies a shareable URL for the currently active file to the clipboard.
import React, { useContext, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ModelContext, FSContext } from './contexts.ts';
export default function ShareButton({ className, style }) {
    const model = useContext(ModelContext);
    if (!model)
        throw new Error('No model');
    const state = model.state;
    const fs = useContext(FSContext);
    const toast = useRef(null);
    const buildShareUrl = () => {
        const base = `${location.protocol}//${location.host}${location.pathname}`;
        const activeSource = state.params.sources.find(src => src.path === state.params.activePath);
        if (!activeSource)
            return base;
        // Remote file — share its URL so others can fetch the same source.
        if (activeSource.url) {
            return `${base}#url=${encodeURIComponent(activeSource.url)}`;
        }
        // If content is loaded, check whether it's been edited vs the library FS.
        if (activeSource.content != null) {
            let unmodified = false;
            if (fs) {
                try {
                    const fsContent = new TextDecoder("utf-8").decode(fs.readFileSync(activeSource.path));
                    unmodified = fsContent === activeSource.content;
                }
                catch {
                    // Not in FS or can't read — must inline the content.
                }
            }
            if (unmodified) {
                return `${base}#path=${encodeURIComponent(activeSource.path)}`;
            }
            // Edited or user-created — inline the content so others see the same code.
            return `${base}#src=${encodeURIComponent(activeSource.content)}`;
        }
        // Content not loaded yet — it's a library file, share by path.
        return `${base}#path=${encodeURIComponent(activeSource.path)}`;
    };
    const copyToClipboard = async () => {
        const url = buildShareUrl();
        try {
            await navigator.clipboard.writeText(url);
            toast.current?.show({
                severity: 'success',
                summary: 'Link copied',
                detail: 'Shareable URL copied to clipboard',
                life: 3000,
            });
        }
        catch {
            // Fallback for browsers without the async clipboard API.
            const textArea = document.createElement('textarea');
            textArea.value = url;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                toast.current?.show({
                    severity: 'success',
                    summary: 'Link copied',
                    detail: 'Shareable URL copied to clipboard',
                    life: 3000,
                });
            }
            catch {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Copy failed',
                    detail: 'Could not copy URL to clipboard',
                    life: 3000,
                });
            }
            document.body.removeChild(textArea);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(Toast, { ref: toast }), _jsx(Button, { icon: "pi pi-share-alt", onClick: copyToClipboard, className: `p-button-sm ${className ?? ''}`, label: "Share", severity: "secondary", title: "Copy shareable link to clipboard", style: style })] }));
}
